<template>
    <div>
      <Header />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">All Transactions</h5>
          <p class="page-sub">Complete record of all financial transactions</p>
        </div>
        <div class="page-actions">
  
          <!-- Search -->
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search transactions..." @input="tableData.page = 1; fetchData()" v-model="tableData.search" />
          </div>
  
          <!-- Export -->
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a class="dropdown-item" href="#" @click="drawPdf(print_content, headerArrayColumn, 'p', 'transactions', 'Transactions')">
                <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
              </a></li>
              <li><a class="dropdown-item" href="#" @click="reusableMixinToCSV('transactions.csv')">
                <font-awesome-icon icon="file-csv" class="me-2 text-success" /> CSV
              </a></li>
            </ul>
          </div>
  
          <!-- New Posting -->
          <router-link :to="$url('/post-transaction')" class="btn-primary-action"
            :class="permission.post_transaction != 1 ? 'disabled' : ''">
            <font-awesome-icon icon="plus" /> New Posting
          </router-link>
  
        </div>
      </div>
  
      <!-- Data Card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading transactions...</p>
        </div>
  
        <!-- 403 -->
        <div v-else-if="permission_status == 403" class="state-block">
          <div class="state-icon state-icon--red">
            <font-awesome-icon icon="lock" />
          </div>
          <p class="state-title">Access Denied</p>
          <p class="state-sub">You don't have permission to view this resource.</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--blue">
            <font-awesome-icon icon="exchange-alt" />
          </div>
          <p class="state-title">No transactions found</p>
          <p class="state-sub">No transactions match your current search.</p>
        </div>
  
        <!-- Table -->
        <template v-else>
  
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong>
            </span>
            <div class="toolbar-right">
              <label class="table-count">Rows:</label>
              <select v-model.number="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
              </select>
            </div>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Reference</th>
                  <th class="text-right">Amount</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th class="col-actions">Detail</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in details" :key="index">
  
                  <td class="text-muted-cell">{{ index + 1 }}</td>
  
                  <td>
                    <span class="batch-code">{{ row.reference }}</span>
                  </td>
  
                  <td class="text-right fw-bold amount-value">
                    {{ formatPrice(row.amount) }}
                  </td>
  
                  <td>
                    <span class="type-badge" :class="typeClass(row.type)">
                      <font-awesome-icon :icon="typeIcon(row.type)" class="me-1" />
                      {{ capitalize(row.type) }}
                    </span>
                  </td>
  
                  <td class="text-muted-cell">{{ formatDate(row.date) }}</td>
  
                  <td class="col-actions">
                    <router-link
                      :to="$url('/transactions/' + row.id)"
                      class="action-btn action-btn--view"
                      title="View detail"
                    >
                      <font-awesome-icon icon="eye" />
                    </router-link>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <div v-if="pagination.lastPage > 1" class="pagination-bar">
            <span class="pagination-info">Showing {{ pagination.from }}–{{ pagination.to }} of {{ pagination.total }}</span>
            <div class="pagination-controls">
              <button class="pg-btn" :disabled="pagination.currentPage <= 1" @click="tableData.page = 1; fetchData()">«</button>
              <button class="pg-btn" :disabled="pagination.currentPage <= 1" @click="tableData.page = pagination.currentPage - 1; fetchData()">‹ Prev</button>
              <span class="pg-pages">
                <button v-for="p in pageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === pagination.currentPage }" @click="tableData.page = p; fetchData()">{{ p }}</button>
              </span>
              <button class="pg-btn" :disabled="pagination.currentPage >= pagination.lastPage" @click="tableData.page = pagination.currentPage + 1; fetchData()">Next ›</button>
              <button class="pg-btn" :disabled="pagination.currentPage >= pagination.lastPage" @click="tableData.page = pagination.lastPage; fetchData()">»</button>
            </div>
          </div>
  
        </template>
      </div>
  
    </div>
  </template>
  
  <script>
  import { reusableMixin } from '../../../lib/reusableMixin';
  import Header from './component/header.vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useStore } from 'vuex';
  import moment from 'moment';
  
  export default {
    components: { Header },
    mixins: [reusableMixin],
  
    data() {
      return {
        print_content: [],
        headerArrayColumn: ['S/N','Reference','Amount','Type','Date'],
        details: [], loading: false,
        permission_status: null,
        tableData: { page: 1,
          draw: 0, length: 5, search: '', column: 0, dir: 'desc', id: '',
        },
        pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
        toast: useToast(),
        store: useStore(),
      };
    },
  
    computed: {
      permission() { return this.store.state.getCurrentUserPermission; },
      pageRange() {
        if (!this.pagination.lastPage) return [];
        const cur = this.pagination.currentPage, last = this.pagination.lastPage;
        const range = [];
        for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i);
        return range;
      },
    },
  
    created() { this.fetchData(); },
  
    methods: {
      dataToPrint(details) {
        this.print_content = details.map((d, idx) => ({
          sn:     idx + 1,
          ref:    d.reference,
          amount: d.amount,
          type:   d.type,
          date:   this.formatDate(d.date),
        }));
      },
  
      fetchData(url = '/accounting/transactions') {
        this.loading = true;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            const data   = response.data;
            this.details = data.data.data;
            this.dataToPrint(this.details);
            this.configPagination(data.data);
            this.loading = false;
          })
          .catch(error => {
            this.loading = false;
            if (error?.response?.status == 403) {
              this.permission_status = 403;
              this.toast.error(error.response.data.message);
            }
          });
      },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
  
      formatDate(date) { return moment(date).format('DD/MM/YYYY'); },
  
      formatPrice(value) {
        if (!value && value !== 0) return '0.00';
        return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
  
      capitalize(text) { return text ? text.charAt(0).toUpperCase() + text.slice(1) : '—'; },
  
      typeClass(type) {
        const map = {
          sales: 'type--sales', purchase: 'type--purchase',
          payroll: 'type--payroll', debit: 'type--debit',
          credit: 'type--credit', manual: 'type--manual',
        };
        return map[type] ?? 'type--default';
      },
  
      typeIcon(type) {
        const map = {
          sales: 'arrow-up', purchase: 'arrow-down',
          payroll: 'users', debit: 'minus-circle',
          credit: 'plus-circle', manual: 'pen',
        };
        return map[type] ?? 'exchange-alt';
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  /* ── Search ── */
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:200px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .search-input::placeholder { color:#aab8cc; }
  
  /* ── Buttons ── */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; text-decoration:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover { background:#003b8e; color:#fff; text-decoration:none; }
  .btn-primary-action.disabled { opacity:.5; pointer-events:none; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-outline:hover { background:#f4f7fb; }
  .btn-outline::after { display:none !important; }
  
  /* ── Data Card ── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── States ── */
  .state-block  { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon   { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .state-icon--red  { background:rgba(224,53,53,.1); color:#e03535; }
  .state-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub    { font-size:13px; color:#7a90b0; margin:0; }
  .mt-3         { margin-top:12px; }
  
  /* ── Toolbar ── */
  .table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count   { font-size:13px; color:#7a90b0; }
  .toolbar-right { display:flex; align-items:center; gap:8px; }
  .rows-select   { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  
  /* ── Table ── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 14px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:12px 14px; font-size:13.5px; vertical-align:middle; }
  .col-actions { width:60px; text-align:center; }
  .text-right      { text-align:right; }
  .text-muted-cell { color:#4a5e78; }
  .fw-bold         { font-weight:700; }
  .amount-value    { color:#004AAD; }
  .me-1            { margin-right:4px; }
  
  /* ── Batch Code ── */
  .batch-code { font-family:monospace; font-size:12.5px; font-weight:600; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }
  
  /* ── Type Badges ── */
  .type-badge     { display:inline-flex; align-items:center; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; }
  .type--sales    { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .type--purchase { background:#fff0f0; color:#e03535; border-color:#fac5c5; }
  .type--payroll  { background:#f5f3ff; color:#7c3aed; border-color:#c4b5fd; }
  .type--debit    { background:#fff0f0; color:#e03535; border-color:#fac5c5; }
  .type--credit   { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .type--manual   { background:#edf4ff; color:#004AAD; border-color:#c5d6f5; }
  .type--default  { background:#f4f7fb; color:#7a90b0; border-color:#e4eaf2; }
  
  /* ── Action Button ── */
  .action-btn  { width:30px; height:30px; border-radius:7px; border:1px solid #e4eaf2; display:flex; align-items:center; justify-content:center; font-size:13px; cursor:pointer; transition:background .15s; text-decoration:none; background:#fff; margin:0 auto; }
  .action-btn--view { color:#0d9488; }
  .action-btn--view:hover { background:#f0fdfb; border-color:#a7f3d0; text-decoration:none; }
  
  /* ── Footer ── */
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  /* ── Dropdown ── */
  .dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:170px; }
  .dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; }
  .dropdown-item:hover { background:#f4f7fb; }
  
  /* ── Responsive ── */
  @media(max-width:991px) { .page-header,.page-actions { flex-direction:column; align-items:stretch; } .search-input { width:100%; } }
  </style>